import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { GitBranch, Sparkles } from "lucide-react";

import { Eyebrow } from "@/components/dravian/eyebrow";
import { WorkspaceShell } from "@/components/dravian/workspace-shell";
import { scenarios as scenariosApi, type ApiScenario } from "@/lib/api";
import { useWorkspaces } from "@/store/workspaces";

export const Route = createFileRoute("/$workspace/scenarios")({
  head: () => ({ meta: [{ title: "Scenarios — Dravian Horizon" }] }),
  component: ScenariosPage,
});

const DOMAIN_EXAMPLE: Record<string, string> = {
  geopolitical:
    "What happens if the ELN-government ceasefire collapses in the next 60 days? Build a scenario tree with branches and probabilities.",
  financial:
    "If our largest counterparty's credit rating drops two notches over the next 90 days, build a scenario tree of downstream exposures.",
  supply_chain:
    "If our Tier-1 supplier in Shenzhen halts production for 30 days, build a scenario tree of fallback options and customer impact.",
  threat:
    "If the threat actor pivots to a new C2 infrastructure over the next 30 days, build a scenario tree of likely targets and indicators.",
  compliance:
    "If the new EU sanctions regime takes effect Q3, build a scenario tree of which products and counterparties fall in scope.",
  custom:
    "Pick a future event you want to stress-test and ask the model to build a scenario tree with branches and probabilities.",
};

function ScenariosPage() {
  const { workspace: id } = useParams({ from: "/$workspace/scenarios" });
  const { getById } = useWorkspaces();
  const ws = getById(id);
  const q = useQuery({
    queryKey: ["scenarios", id],
    queryFn: () => scenariosApi.list(id),
  });

  const scenarios = q.data ?? [];
  const example = DOMAIN_EXAMPLE[ws?.domain ?? "custom"] ?? DOMAIN_EXAMPLE.custom;

  return (
    <WorkspaceShell>
      <div className="p-5 max-w-[1200px] mx-auto">
        <div className="label-eyebrow mb-1">Scenarios</div>
        <h2 className="text-[20px] font-semibold mb-5">Scenario Library</h2>

        <div className="panel p-5 mb-6">
          <Eyebrow right={<Sparkles className="h-3.5 w-3.5 text-primary" />}>
            Simulate a new scenario
          </Eyebrow>
          <p className="text-[12px] text-muted-foreground mb-2">
            Open the chat panel on the right and describe a future event. The
            configured AI model will build a probability-weighted tree you can
            inspect on the Scenario Tree canvas.
          </p>
          <div className="rounded-md border border-border bg-surface-elevated/40 px-3 py-2 text-[12px] text-foreground/80 italic">
            "{example}"
          </div>
        </div>

        {q.isLoading ? (
          <div className="panel p-6 text-center text-[12px] text-muted-foreground">
            Loading scenarios…
          </div>
        ) : scenarios.length === 0 ? (
          <div className="panel p-8 text-center">
            <GitBranch className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
            <div className="text-[14px] font-semibold mb-1">No scenarios yet</div>
            <p className="text-[12px] text-muted-foreground max-w-md mx-auto">
              Ask the chat to simulate a future event — every scenario it builds
              lands here and on the Scenario Tree canvas.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {scenarios.map((s) => (
              <ScenarioCard key={s.id} scenario={s} workspaceId={id} />
            ))}
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

function ScenarioCard({
  scenario,
  workspaceId,
}: {
  scenario: ApiScenario;
  workspaceId: string;
}) {
  // Find the most-probable leaf and use its impact for the badge
  const leaves: { impact: string | null; probability: number }[] = [];
  const walk = (
    n: { children: typeof scenario.tree.children; impact: string | null; probability: number },
    accProb: number,
  ) => {
    const next = accProb * n.probability;
    if (!n.children || n.children.length === 0) {
      leaves.push({ impact: n.impact, probability: next });
    } else {
      n.children.forEach((c) => walk(c, next));
    }
  };
  walk(scenario.tree, 1);
  leaves.sort((a, b) => b.probability - a.probability);
  const topImpact = leaves[0]?.impact ?? null;

  const badgeColor =
    topImpact === "critical"
      ? "text-[#7f1d1d]"
      : topImpact === "high"
        ? "text-risk-high"
        : topImpact === "medium"
          ? "text-risk-medium"
          : "text-risk-low";

  const horizonLabel =
    scenario.horizon_days >= 180
      ? `Next ${Math.round(scenario.horizon_days / 30)} months`
      : `Next ${scenario.horizon_days} days`;

  const created = new Date(scenario.created_at);
  const createdRel = relative(created);

  return (
    <div className="panel p-4 flex flex-col">
      <div className="flex items-center justify-between">
        <Eyebrow>{horizonLabel}</Eyebrow>
        <span className="text-[10px] text-muted-foreground font-mono">{createdRel}</span>
      </div>
      <h3 className="text-[15px] font-semibold mt-1">{scenario.name}</h3>
      {scenario.description && (
        <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed line-clamp-3">
          {scenario.description}
        </p>
      )}
      <div className="text-[10px] text-muted-foreground mt-2 line-clamp-2">
        <span className="font-mono uppercase tracking-wider mr-1">Initial:</span>
        {scenario.initial_event}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        {topImpact ? (
          <span className={`text-[10px] uppercase tracking-wider font-semibold ${badgeColor}`}>
            top branch · {topImpact} impact
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            no leaves
          </span>
        )}
        <Link
          to="/$workspace/scenario-tree"
          params={{ workspace: workspaceId }}
          className="text-[11px] text-primary hover:underline"
        >
          Open →
        </Link>
      </div>
    </div>
  );
}

function relative(d: Date): string {
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}
